import {
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import React, { useState } from 'react';
import { AssignPersonForm } from 'src/components/oneCorp/features/MealCredit/components/People/AssignPersonForm/AssignPersonForm';
import Table from 'src/components/Table/Table';
import { Pagination } from 'src/components/Pagination/Pagination';
import { MEAL_CREDIT_DINERS_PAGE_SIZE } from 'src/components/oneCorp/features/MealCredit/hooks/useMealCreditDinerSearch';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { ILocDiner } from 'src/utilities/useSearchHooks';
import { PeopleTableItem } from './PeopleTableItem';

export interface PeopleTableProps {
  totalResults: number;
  resultsList: ILocDiner[];
  totalPages: number;
  currentSearchPage: number;
  pageChange: (page: number) => void;
  hasNoResultsWithQuery: boolean;
  hasNoResultsWithoutQuery: boolean;
  isLoading: boolean;
  isRemoving: boolean;
  isAdding: boolean;
  removePeople: (dinersToRemove: string[]) => Promise<void>;
  addPeople: (dinersToAdd: string[]) => Promise<void>;
}

export function PeopleTable({
  totalResults,
  resultsList,
  totalPages,
  currentSearchPage,
  pageChange,
  hasNoResultsWithQuery,
  hasNoResultsWithoutQuery,
  isLoading,
  isRemoving,
  isAdding,
  removePeople,
  addPeople,
}: PeopleTableProps): JSX.Element {
  const [currentPersonIdRemoved, setCurrentPersonIdRemoved] = useState(null);
  const [showAddPersonForm, setShowAddPersonForm] = useState(false);

  const onRemovePersonHandle = async (id: string) => {
    setCurrentPersonIdRemoved(id);
    await removePeople([id]);
  };

  const addPeopleHandle = async (id: string) => {
    await addPeople([id]);
  };

  return (
    <>
      <Stack y={totalPages > 1 ? 'md' : 'none'}>
        <Table
          isLoading={isLoading}
          headerInsets="sm"
          header={() => (
            <Row>
              <Column phone={4}>
                <Copy variant="body" color="secondaryText">
                  Name
                </Copy>
              </Column>
              <Column phone={4}>
                <Copy variant="body" color="secondaryText">
                  Email
                </Copy>
              </Column>
            </Row>
          )}
          body={() => (
            <>
              {resultsList.map((person, index) => {
                return (
                  <Square
                    inset="sm"
                    key={person.id}
                    borderBottom={
                      index === resultsList.length - 1 ? 'none' : '1px solid'
                    }
                    borderColor="lightLine"
                  >
                    <PeopleTableItem
                      isRemoving={
                        isRemoving && person.id === currentPersonIdRemoved
                      }
                      isDisabled={isAdding || isRemoving}
                      onRemovePerson={async () =>
                        onRemovePersonHandle(person.id)
                      }
                      name={person.name}
                      email={person.email}
                    />
                  </Square>
                );
              })}
              {hasNoResultsWithoutQuery && (
                <NoResultsMsg message="You haven’t selected people yet." />
              )}
              {hasNoResultsWithQuery && (
                <NoResultsMsg message="We couldn’t find what you were looking for. Give it another go." />
              )}
            </>
          )}
          showFooter
          footer={() => (
            <Square inset="sm" width="100%">
              {showAddPersonForm ? (
                <AssignPersonForm
                  onFormSubmit={addPeopleHandle}
                  onCancel={() => setShowAddPersonForm(false)}
                />
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setShowAddPersonForm(true)}
                  disabled={isRemoving}
                >
                  Assign person
                </Button>
              )}
            </Square>
          )}
        />
      </Stack>
      {totalPages > 1 && (
        <Flex justifyContent="center">
          <Pagination
            maxSize={8}
            pageSize={MEAL_CREDIT_DINERS_PAGE_SIZE}
            boundaryLinks
            page={currentSearchPage}
            collectionSize={totalResults}
            pageChange={(page) => {
              pageChange(page);
            }}
          />
        </Flex>
      )}
    </>
  );
}
